import React, { useMemo, useState, useEffect } from "react";
import {
  Box, Typography, MenuItem, TextField, Select, FormControl, InputLabel, Button, Chip, Menu, IconButton, Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/BookmarkAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
} from "recharts";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { C, bandColor, bandLabel, fmtUSD, fmtFull, exportCSV, exportExcel, exportPDF } from "./reportingUtils";
import { nlReportQuery } from "../api/claudeApi";
import { toast } from "react-toastify";

const METRICS = [
  { key: "count", label: "Counterparties (count)", agg: (g) => g.length, money: false },
  { key: "recExposure", label: "Recommended exposure", agg: (g) => g.reduce((a, r) => a + r.recLimit, 0), money: true },
  { key: "appExposure", label: "Approved exposure", agg: (g) => g.reduce((a, r) => a + r.appLimit, 0), money: true },
  { key: "avgScore", label: "Average ARC score", agg: (g) => { const s = g.filter((r) => r.score != null); return s.length ? +(s.reduce((a, r) => a + r.score, 0) / s.length).toFixed(2) : 0; }, money: false },
];
const DIMENSIONS = [
  { key: "sector", label: "Sector", of: (r) => r.sector || "Unspecified" },
  { key: "region", label: "Region", of: (r) => r.region || "Other" },
  { key: "country", label: "Country", of: (r) => r.country || "Unknown" },
  { key: "status", label: "Approval status", of: (r) => (r.status || "PENDING").toString() },
  { key: "band", label: "Risk band", of: (r) => bandLabel(r.score) },
  { key: "none", label: "No grouping (total)", of: () => "All counterparties" },
];
const CHART_TYPES = ["Bar", "Pie", "Table"];
const PIE_COLORS = [C.ink, C.blue, C.green, C.amber, C.violet, C.red, C.muted, "#0EA5A5", "#D9488A"];
const LS_KEY = "crediarc_bi_report_templates";

const loadTemplates = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; } };
const saveTemplates = (t) => { try { localStorage.setItem(LS_KEY, JSON.stringify(t)); } catch { /* ignore */ } };

const ReportBuilder = ({ rows, onDrill }) => {
  const [metricKey, setMetricKey] = useState("recExposure");
  const [dimKey, setDimKey] = useState("sector");
  const [chartType, setChartType] = useState("Bar");
  const [minScore, setMinScore] = useState("");
  const [maxScore, setMaxScore] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [templates, setTemplates] = useState(loadTemplates());
  const [exportEl, setExportEl] = useState(null);
  const [nlText, setNlText] = useState("");
  const [nlLoading, setNlLoading] = useState(false);

  const applySpec = (spec) => {
    if (!spec) return;
    if (spec.metric) setMetricKey(spec.metric);
    if (spec.dimension) setDimKey(spec.dimension);
    if (spec.chartType) setChartType(spec.chartType);
    setMinScore(spec.filters?.minScore != null ? String(spec.filters.minScore) : "");
    setMaxScore(spec.filters?.maxScore != null ? String(spec.filters.maxScore) : "");
    setStatusFilter(spec.filters?.status || "All");
  };
  const runNL = async () => {
    const q = nlText.trim();
    if (!q || nlLoading) return;
    setNlLoading(true);
    try {
      const spec = await nlReportQuery(q);
      if (spec) applySpec(spec);
      else toast.info("Couldn't interpret that — try rephrasing.");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Couldn't run that query.");
    } finally {
      setNlLoading(false);
    }
  };

  const metric = METRICS.find((m) => m.key === metricKey);
  const dim = DIMENSIONS.find((d) => d.key === dimKey);

  const filtered = useMemo(() => {
    return (rows || []).filter((r) => {
      if (minScore !== "" && (r.score == null || r.score < parseFloat(minScore))) return false;
      if (maxScore !== "" && (r.score == null || r.score > parseFloat(maxScore))) return false;
      if (statusFilter !== "All" && (r.status || "PENDING").toUpperCase() !== statusFilter.toUpperCase()) return false;
      return true;
    });
  }, [rows, minScore, maxScore, statusFilter]);

  const data = useMemo(() => {
    const groups = {};
    filtered.forEach((r) => { const k = dim.of(r); (groups[k] = groups[k] || []).push(r); });
    return Object.entries(groups)
      .map(([name, g]) => ({ name, value: metric.agg(g), count: g.length, _rows: g }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 14);
  }, [filtered, dim, metric]);

  const fmtVal = (v) => (metric.money ? fmtUSD(v) : v);
  const statuses = useMemo(() => ["All", ...Array.from(new Set((rows || []).map((r) => (r.status || "PENDING"))))], [rows]);

  const exportHeaders = [{ key: "name", label: dim.label }, { key: "value", label: metric.label }, { key: "count", label: "Counterparties" }];
  const exportData = data.map((d) => ({ name: d.name, value: metric.money ? Math.round(d.value) : d.value, count: d.count }));
  const fname = `CrediArc-Report-${metric.key}-by-${dim.key}`;
  const doExport = (fmt) => {
    setExportEl(null);
    const title = `${metric.label} by ${dim.label}`;
    if (fmt === "csv") exportCSV(exportData, exportHeaders, fname);
    else if (fmt === "xlsx") exportExcel(exportData, exportHeaders, fname, "Report");
    else exportPDF(exportData, exportHeaders, fname, title, `Filtered to ${filtered.length} counterparties`);
  };

  const doSave = () => {
    const name = window.prompt("Name this report template:");
    if (!name) return;
    const tpl = { id: `${Date.now()}`, name, metricKey, dimKey, chartType, minScore, maxScore, statusFilter };
    const next = [...templates.filter((t) => t.name !== name), tpl];
    setTemplates(next); saveTemplates(next);
  };
  const applyTemplate = (t) => { setMetricKey(t.metricKey); setDimKey(t.dimKey); setChartType(t.chartType); setMinScore(t.minScore || ""); setMaxScore(t.maxScore || ""); setStatusFilter(t.statusFilter || "All"); };
  const delTemplate = (id) => { const next = templates.filter((t) => t.id !== id); setTemplates(next); saveTemplates(next); };
  useEffect(() => { /* keep in sync if another tab changed storage */ }, []);

  const sel = (label, value, onChange, options) => (
    <FormControl size="small" sx={{ minWidth: 170 }}>
      <InputLabel sx={{ fontSize: 13 }}>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ fontSize: 13.5, background: "#fff" }}>
        {options.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13.5 }}>{o.label}</MenuItem>)}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {/* controls */}
      <Box sx={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px", p: "16px 18px" }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5, pb: 1.5, borderBottom: `1px solid ${C.line}` }}>
          <AutoAwesomeIcon sx={{ color: C.violet, flex: "0 0 auto" }} />
          <TextField value={nlText} onChange={(e) => setNlText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") runNL(); }}
            placeholder={'Ask in plain English — e.g. "recommended exposure by region for high-risk companies"'} size="small" fullWidth
            sx={{ "& .MuiOutlinedInput-root": { fontSize: 13.5 } }} />
          <Button onClick={runNL} disabled={nlLoading} variant="contained"
            sx={{ textTransform: "none", background: C.violet, whiteSpace: "nowrap", "&:hover": { background: "#6a4ae0" } }}>
            {nlLoading ? "Interpreting…" : "Ask AI"}
          </Button>
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.ink, mb: 1.5 }}>Build a report</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center" }}>
          {sel("Measure", metricKey, setMetricKey, METRICS.map((m) => ({ value: m.key, label: m.label })))}
          {sel("Group by", dimKey, setDimKey, DIMENSIONS.map((d) => ({ value: d.key, label: d.label })))}
          {sel("Chart", chartType, setChartType, CHART_TYPES.map((t) => ({ value: t, label: t })))}
          {sel("Status", statusFilter, setStatusFilter, statuses.map((s) => ({ value: s, label: s })))}
          <TextField label="Min ARC" size="small" value={minScore} onChange={(e) => setMinScore(e.target.value)} sx={{ width: 92 }} inputProps={{ inputMode: "decimal" }} />
          <TextField label="Max ARC" size="small" value={maxScore} onChange={(e) => setMaxScore(e.target.value)} sx={{ width: 92 }} inputProps={{ inputMode: "decimal" }} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Save this configuration as a reusable template"><Button startIcon={<SaveIcon />} onClick={doSave} sx={{ textTransform: "none", color: C.ink }}>Save template</Button></Tooltip>
          <Button startIcon={<DownloadIcon />} variant="outlined" onClick={(e) => setExportEl(e.currentTarget)} sx={{ textTransform: "none", borderColor: C.line, color: C.ink }}>Export</Button>
          <Menu anchorEl={exportEl} open={!!exportEl} onClose={() => setExportEl(null)}>
            <MenuItem onClick={() => doExport("csv")}>CSV</MenuItem><MenuItem onClick={() => doExport("xlsx")}>Excel</MenuItem><MenuItem onClick={() => doExport("pdf")}>PDF</MenuItem>
          </Menu>
        </Box>
        {templates.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.5, alignItems: "center" }}>
            <Typography sx={{ fontSize: 11.5, color: C.muted, mr: 0.5 }}>Saved:</Typography>
            {templates.map((t) => (
              <Chip key={t.id} label={t.name} onClick={() => applyTemplate(t)} onDelete={() => delTemplate(t.id)}
                deleteIcon={<DeleteOutlineIcon />} size="small" sx={{ fontSize: 12, background: "#EEF4FF", color: C.ink, "& .MuiChip-deleteIcon": { color: C.muted } }} />
            ))}
          </Box>
        )}
      </Box>

      {/* result */}
      <Box sx={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: "12px", p: "18px 20px" }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{metric.label} by {dim.label}</Typography>
        <Typography sx={{ fontSize: 12.5, color: C.muted, mb: 1.5 }}>{filtered.length} of {(rows || []).length} counterparties after filters · click a {chartType === "Table" ? "row" : "segment"} to drill in</Typography>

        {data.length === 0 && <Typography sx={{ fontSize: 13, color: C.muted, py: 4, textAlign: "center" }}>No data for these filters.</Typography>}

        {chartType === "Bar" && data.length > 0 && (
          <ResponsiveContainer width="100%" height={Math.max(240, data.length * 34)}>
            <BarChart layout="vertical" data={data} margin={{ left: 8, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F7" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: C.muted }} tickFormatter={metric.money ? fmtUSD : undefined} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10.5, fill: C.muted }} width={140} />
              <RTooltip formatter={(v) => [metric.money ? fmtFull(v) : v, metric.label]} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} cursor="pointer" onClick={(d) => onDrill?.(`${d.name} · ${dim.label}`, d._rows)}>
                {data.map((d, i) => <Cell key={i} fill={metricKey === "avgScore" ? bandColor(d.value) : C.blue} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}

        {chartType === "Pie" && data.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}
                onClick={(d) => onDrill?.(`${d.name} · ${dim.label}`, d._rows)} cursor="pointer">
                {data.map((d, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <RTooltip formatter={(v) => [metric.money ? fmtFull(v) : v, metric.label]} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        )}

        {chartType === "Table" && data.length > 0 && (
          <Box sx={{ border: `1px solid ${C.line}`, borderRadius: "8px", overflow: "hidden" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", background: C.ground, px: 1.25, py: 0.75 }}>
              {[dim.label, metric.label, "Counterparties"].map((h) => <Typography key={h} sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: C.muted }}>{h}</Typography>)}
            </Box>
            {data.map((d, i) => (
              <Box key={i} onClick={() => onDrill?.(`${d.name} · ${dim.label}`, d._rows)}
                sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", px: 1.25, py: 0.9, borderTop: `1px solid ${C.line}`, cursor: "pointer", "&:hover": { background: "#F7F9FE" } }}>
                <Typography sx={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{d.name}</Typography>
                <Typography sx={{ fontSize: 13, color: C.slate, fontVariantNumeric: "tabular-nums" }}>{metric.money ? fmtFull(d.value) : d.value}</Typography>
                <Typography sx={{ fontSize: 13, color: C.slate }}>{d.count}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReportBuilder;
